import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  PostApiV5FirmsRfiDocumentsBodySourceKind,
  RfiDocumentResponse,
  useGetApiV5FirmsRfiDocuments,
  useGetApiV5FirmsRfiTemplates,
  usePostApiV5FirmsRfiDocuments,
} from '@liscio/api';
import { useTrackEvent } from '@liscio/common';
import { Button, Icon, Stack, Typography } from '@liscio/ui';

import type { SmartTaxOrganizerCompletionResult } from './types';
import {
  WHAT_TO_DO_TODAY_COMING_FROM,
  WHAT_TO_DO_TODAY_DASHBOARD_STEPS,
  WHAT_TO_DO_TODAY_EVENTS,
  WHAT_TO_DO_TODAY_SOURCE,
} from '../constants';
import { fetchSamplePdf } from '../sampleDocuments';
import { config } from 'config';
import { useProfileData } from 'fetch-utils/users/user-hooks';
import { SmartTaxOrganizerUploadCard } from 'modules/smart-tax/components/SmartTaxImport/shared/SmartTaxOrganizerUploadCard';
import { SmartTaxReturnUploadCard } from 'modules/smart-tax/components/SmartTaxImport/shared/SmartTaxReturnUploadCard';
import {
  FileError,
  useActiveStorageFileUploader,
} from 'modules/smart-tax/hooks/useActiveStorageFileUploader';
import { SYNTHETIC_RETURN_SAMPLE } from 'modules/smart-tax/utils/sampleDocuments';

const MAX_FILES = 1;
const POLLING_INTERVAL_MS = 3_000;
const RFI_UPLOAD_URL = `${config.api.url}/api/v5/firms/rfi/direct_uploads`;

interface OrganizerSample {
  key: string;
  label: string;
  filename: string;
  path: string;
  templateName?: string;
}

const ORGANIZER_SAMPLES: OrganizerSample[] = [
  {
    key: 'cch_2025',
    label: 'CCH',
    filename: '2025_Organizer_CCH_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_CCH_Sample.pdf',
    templateName: 'CCH 2025 Organizer',
  },
  {
    key: 'drake_2025',
    label: 'Drake',
    filename: '2025_Organizer_Drake_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_Drake_Sample.pdf',
    templateName: 'Drake 2025 Organizer',
  },
  {
    key: 'lacerte_2025',
    label: 'Lacerte',
    filename: '2025_Organizer_Lacerte_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_Lacerte_Sample.pdf',
    templateName: 'Lacerte 2025 Organizer',
  },
  {
    key: 'proseries_2025',
    label: 'ProSeries',
    filename: '2025_Organizer_ProSeries_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_ProSeries_Sample.pdf',
    templateName: 'ProSeries 2025 Organizer',
  },
  {
    key: 'ultratax_2025',
    label: 'UltraTax',
    filename: '2025_Organizer_UltraTax_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_UltraTax_Sample.pdf',
    templateName: 'UltraTax 2025 Organizer',
  },
  {
    key: 'ultratax_lite_2025',
    label: 'UltraTax Lite',
    filename: '2025_Organizer_UltraTax_Lite_Sample.pdf',
    path: '/samples/smart-tax-organizer/2025_Organizer_UltraTax_Lite_Sample.pdf',
    templateName: 'UltraTax 2025 Lite Organizer',
  },
];

const normalizeTemplateName = (name: string) =>
  name.trim().toLocaleLowerCase().replace(/\s+/g, ' ');

const ORGANIZER_BENEFITS: Array<{
  icon: React.ComponentProps<typeof Icon>['icon'];
  title: string;
  description: string;
}> = [
  {
    icon: 'upload',
    title: 'Upload once',
    description:
      'Drop in the organizer PDF from CCH, Drake, Lacerte, ProSeries, or UltraTax',
  },
  {
    icon: 'taxDoc',
    title: 'AI does the busywork',
    description:
      'Liscio reads it and builds a personalized organizer for your client',
  },
  {
    icon: 'user',
    title: 'Clients love it',
    description:
      'They complete it online, no printing or scanning 40-page PDFs',
  },
  {
    icon: 'check',
    title: 'Nothing slips',
    description: "Auto-reminders chase missing items so you don't have to",
  },
];

const RETURN_ORGANIZER_BENEFITS: typeof ORGANIZER_BENEFITS = [
  {
    icon: 'upload',
    title: 'Upload one return',
    description: 'Use a text-based Form 1040 with supporting schedules',
  },
  {
    icon: 'taxDoc',
    title: 'Start from prior-year data',
    description: 'Liscio extracts supported values and identifies omissions',
  },
  {
    icon: 'check',
    title: 'Keep control',
    description: 'The generated organizer remains a draft until you send it',
  },
];

type ProcessingStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'failed';

export interface SmartTaxOrganizerModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: (result: SmartTaxOrganizerCompletionResult) => void;
}

function isFileError(file: ActiveStorage.Blob | FileError): file is FileError {
  return (file as FileError).error === true;
}

function useSmartTaxOrganizerUpload(
  isActive: boolean,
  onComplete: (result: SmartTaxOrganizerCompletionResult) => void
) {
  const navigate = useNavigate();
  const { trackEvent } = useTrackEvent();
  const { enableRfiSystem = false, enableTaxReturnOrganizerPilot = false } =
    useFlags();
  const isTaxReturnPilot = enableRfiSystem && enableTaxReturnOrganizerPilot;
  const { cpa_user_id } = useProfileData();
  const dedupeListRef = useRef<File[]>([]);
  const hasProcessedUploadRef = useRef(false);
  const completionFiredRef = useRef(false);
  const processingFileCountRef = useRef(0);
  const sampleKeyRef = useRef<string | null>(null);
  const organizerSourceKindRef = useRef<
    typeof PostApiV5FirmsRfiDocumentsBodySourceKind.tax_return | undefined
  >();
  const sampleSelectionIdRef = useRef(0);
  const [shouldStartUpload, setShouldStartUpload] = useState(false);
  const [processingStatus, setProcessingStatus] =
    useState<ProcessingStatus>('idle');
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState('');
  const [rfiDocumentUuid, setRfiDocumentUuid] = useState<string | null>(null);
  const [selectedSampleFile, setSelectedSampleFile] = useState<File | null>(
    null
  );
  const [selectedSampleKey, setSelectedSampleKey] = useState<string | null>(
    null
  );
  const [isLoadingSample, setIsLoadingSample] = useState(false);

  const { data: templatesData, isLoading: isLoadingTemplates } =
    useGetApiV5FirmsRfiTemplates(
      { status: 'published' },
      { query: { enabled: enableRfiSystem } }
    );
  const templates = useMemo(
    () => templatesData?.data ?? [],
    [templatesData?.data]
  );
  const selectedTemplate = templates.find(
    (template) => template.uuid === selectedTemplateUuid
  );

  const { mutateAsync: createRfiDocument } = usePostApiV5FirmsRfiDocuments();
  const {
    files,
    addFiles,
    uploadFiles,
    uploadedFiles,
    uploadStatus,
    isUploading,
  } = useActiveStorageFileUploader(RFI_UPLOAD_URL);

  const ownerId = cpa_user_id == null ? null : String(cpa_user_id);
  const hasOwner = ownerId != null && ownerId.length > 0;
  const canStageFiles = enableRfiSystem && hasOwner;
  const canUpload = canStageFiles && selectedTemplateUuid.length > 0;

  const { data: rfiDocumentsResponse } = useGetApiV5FirmsRfiDocuments(
    { per_page: 20 },
    {
      query: {
        enabled: Boolean(rfiDocumentUuid) && isActive,
        refetchInterval: (data) => {
          const rfiDocument = data?.data?.find(
            (document) => document.uuid === rfiDocumentUuid
          );

          if (
            rfiDocument?.status === 'completed' ||
            rfiDocument?.status === 'failed'
          ) {
            return false;
          }

          return POLLING_INTERVAL_MS;
        },
      },
    }
  );

  const rfiDocument = useMemo(
    () =>
      rfiDocumentsResponse?.data?.find(
        (document) => document.uuid === rfiDocumentUuid
      ),
    [rfiDocumentUuid, rfiDocumentsResponse?.data]
  );

  const handleTemplateChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setSelectedTemplateUuid(event.target.value);
    },
    []
  );

  // A return can start from any published template, so the pilot preselects
  // the first one. Vendor organizers need the template matching their software.
  useEffect(() => {
    if (isTaxReturnPilot && !selectedTemplateUuid && templates[0]) {
      setSelectedTemplateUuid(templates[0].uuid);
    }
  }, [isTaxReturnPilot, selectedTemplateUuid, templates]);

  const stageUpload = useCallback(
    (fileArray: File[], sampleKey: string | null) => {
      if (!enableRfiSystem) {
        toast.error('Smart Tax Organizer templates are not available.');
        return;
      }

      if (!hasOwner) {
        toast.error('Unable to determine the current owner for this upload.');
        return;
      }

      if (!selectedTemplateUuid) {
        toast.error('Select an organizer template before uploading.');
        return;
      }

      const nextFile = fileArray[0];

      if (!nextFile) {
        return;
      }

      if (dedupeListRef.current.length >= MAX_FILES) {
        toast.error('Only one organizer can be uploaded here.');
        return;
      }

      setProcessingError(null);
      setProcessingStatus('idle');
      setRfiDocumentUuid(null);
      completionFiredRef.current = false;
      hasProcessedUploadRef.current = false;
      sampleKeyRef.current = sampleKey;
      organizerSourceKindRef.current = isTaxReturnPilot
        ? PostApiV5FirmsRfiDocumentsBodySourceKind.tax_return
        : undefined;
      dedupeListRef.current = [nextFile];
      addFiles([nextFile]);
      toast.success('1 file added');
      setShouldStartUpload(true);
    },
    [
      addFiles,
      enableRfiSystem,
      hasOwner,
      isTaxReturnPilot,
      selectedTemplateUuid,
    ]
  );

  const handleDrop = useCallback(
    (fileArray: File[]) => {
      sampleSelectionIdRef.current += 1;
      setIsLoadingSample(false);
      setSelectedSampleFile(null);
      setSelectedSampleKey(null);
      stageUpload(fileArray, null);
    },
    [stageUpload]
  );

  const handleSampleSelect = useCallback(
    async (sample: OrganizerSample) => {
      if (isLoadingTemplates) return;

      const selectionId = sampleSelectionIdRef.current + 1;
      sampleSelectionIdRef.current = selectionId;
      setIsLoadingSample(true);
      setProcessingError(null);

      try {
        const file = await fetchSamplePdf(sample);
        if (sampleSelectionIdRef.current !== selectionId) return;

        const templateName = sample.templateName;
        const matchingTemplate = templateName
          ? templates.find(
              ({ name }) =>
                normalizeTemplateName(name) ===
                normalizeTemplateName(templateName)
            )
          : undefined;

        setSelectedSampleFile(file);
        setSelectedSampleKey(sample.key);
        if (matchingTemplate) {
          setSelectedTemplateUuid(matchingTemplate.uuid);
        } else if (sample.templateName) {
          setSelectedTemplateUuid('');
          setProcessingError(
            `${sample.templateName} is not published. Select a compatible template manually.`
          );
        } else if (!selectedTemplateUuid) {
          setProcessingError(
            'Select a published organizer template before uploading this return sample.'
          );
        }
      } catch {
        if (sampleSelectionIdRef.current !== selectionId) return;

        setSelectedSampleFile(null);
        setSelectedSampleKey(null);
        setProcessingError('Unable to load the selected organizer sample.');
      } finally {
        if (sampleSelectionIdRef.current === selectionId) {
          setIsLoadingSample(false);
        }
      }
    },
    [isLoadingTemplates, selectedTemplateUuid, templates]
  );

  const uploadSelectedSample = useCallback(() => {
    if (selectedSampleFile && selectedSampleKey) {
      if (
        (selectedSampleKey === SYNTHETIC_RETURN_SAMPLE.key) !==
        isTaxReturnPilot
      ) {
        toast.error('Choose a sample for the current organizer source.');
        return;
      }
      stageUpload([selectedSampleFile], selectedSampleKey);
    }
  }, [isTaxReturnPilot, selectedSampleFile, selectedSampleKey, stageUpload]);

  useEffect(() => {
    if (!shouldStartUpload || files.length === 0 || isUploading) {
      return;
    }

    setShouldStartUpload(false);
    hasProcessedUploadRef.current = false;
    setProcessingError(null);
    setProcessingStatus('uploading');
    uploadFiles();
  }, [files.length, isUploading, shouldStartUpload, uploadFiles]);

  const uploadFileMetadata = useCallback(
    async (uFiles: (ActiveStorage.Blob | FileError)[]) => {
      if (!ownerId || !selectedTemplateUuid) {
        if (!isActive) return;
        setProcessingError('Select an organizer template before uploading.');
        setProcessingStatus('failed');
        return;
      }

      if (
        organizerSourceKindRef.current ===
          PostApiV5FirmsRfiDocumentsBodySourceKind.tax_return &&
        (!enableRfiSystem || !enableTaxReturnOrganizerPilot)
      ) {
        if (!isActive) return;
        setProcessingError(
          'Tax return organizer pilot is no longer available.'
        );
        setProcessingStatus('failed');
        return;
      }

      if (isActive) setProcessingStatus('processing');

      const results = await Promise.allSettled(
        uFiles.map((file) => {
          if (isFileError(file)) {
            return Promise.reject(file);
          }

          return createRfiDocument({
            data: {
              template_uuid: selectedTemplateUuid,
              signed_blob_id: file.signed_id,
              cpa_user_id: ownerId,
              ...(organizerSourceKindRef.current
                ? { source_kind: organizerSourceKindRef.current }
                : {}),
              ...(sampleKeyRef.current
                ? { sample_key: sampleKeyRef.current }
                : {}),
            },
          });
        })
      );

      if (!isActive) {
        return;
      }

      const fulfilled = results.filter(
        (result): result is PromiseFulfilledResult<RfiDocumentResponse> =>
          result.status === 'fulfilled'
      );
      const errorCount = results.length - fulfilled.length;

      if (errorCount > 0) {
        toast.error(
          `${errorCount} file${
            errorCount > 1 ? 's' : ''
          } failed to submit for processing`
        );
      }

      if (fulfilled.length === 0) {
        setProcessingError('Smart Tax Organizer submission failed.');
        setProcessingStatus('failed');
        return;
      }

      const firstDocument = fulfilled[0].value.data;
      if (!firstDocument?.uuid) {
        setProcessingError(
          'The organizer was submitted, but no processing ID was returned.'
        );
        setProcessingStatus('failed');
        return;
      }

      processingFileCountRef.current = fulfilled.length;
      toast.success(
        `${fulfilled.length} organizer${
          fulfilled.length > 1 ? 's' : ''
        } submitted for processing.`
      );
      setRfiDocumentUuid(firstDocument.uuid);
    },
    [
      createRfiDocument,
      enableRfiSystem,
      enableTaxReturnOrganizerPilot,
      isActive,
      ownerId,
      selectedTemplateUuid,
    ]
  );

  useEffect(() => {
    if (uploadStatus.success && !hasProcessedUploadRef.current && ownerId) {
      hasProcessedUploadRef.current = true;
      uploadFileMetadata(uploadedFiles);
    }
  }, [ownerId, uploadFileMetadata, uploadedFiles, uploadStatus.success]);

  useEffect(() => {
    if (!isActive || !rfiDocument || completionFiredRef.current) {
      return;
    }

    if (rfiDocument.status === 'completed') {
      const rfiRequestUuid = rfiDocument.rfi_request_uuid;

      if (!rfiRequestUuid) {
        setProcessingError(
          'Smart Tax Organizer finished processing, but no organizer request was returned.'
        );
        setProcessingStatus('failed');
        return;
      }

      completionFiredRef.current = true;
      setProcessingStatus('completed');
      trackEvent(WHAT_TO_DO_TODAY_EVENTS.smartTaxOrganizerDocumentProcessed, {
        source: WHAT_TO_DO_TODAY_SOURCE,
        dashboard_step: WHAT_TO_DO_TODAY_DASHBOARD_STEPS.smartTaxOrganizer,
        rfi_document_uuid: rfiDocumentUuid,
        rfi_request_uuid: rfiRequestUuid,
        template_uuid: selectedTemplateUuid,
        template_name:
          selectedTemplate?.name ?? rfiDocument.template_name ?? null,
        file_count: processingFileCountRef.current,
        source_kind: organizerSourceKindRef.current ?? 'organizer',
        is_synthetic_sample:
          sampleKeyRef.current === SYNTHETIC_RETURN_SAMPLE.key,
      });
      onComplete({
        rfiDocumentUuid,
        rfiRequestUuid,
        templateUuid: selectedTemplateUuid,
        templateName:
          selectedTemplate?.name ?? rfiDocument.template_name ?? null,
        fileCount: processingFileCountRef.current,
      });
      navigate(
        `/rfi-requests/${rfiRequestUuid}?comingFrom=${WHAT_TO_DO_TODAY_COMING_FROM}&guidedExperience=true`
      );
      return;
    }

    if (rfiDocument.status === 'failed') {
      setProcessingError(
        rfiDocument.error_summary?.action ??
          rfiDocument.error_summary?.message ??
          'The uploaded organizer could not be processed.'
      );
      setProcessingStatus('failed');
    }
  }, [
    navigate,
    isActive,
    onComplete,
    rfiDocument,
    rfiDocumentUuid,
    selectedTemplate,
    selectedTemplateUuid,
    trackEvent,
  ]);

  const isUploadInProgress =
    isUploading ||
    shouldStartUpload ||
    processingStatus === 'uploading' ||
    processingStatus === 'processing' ||
    processingStatus === 'completed';

  return {
    templates,
    selectedTemplateUuid,
    handleTemplateChange,
    handleDrop,
    handleSampleSelect,
    uploadSelectedSample,
    isLoadingTemplates,
    isUploadInProgress,
    processingStatus,
    processingError,
    canStageFiles,
    canUpload,
    selectedSampleKey,
    isLoadingSample,
    isTaxReturnPilot,
  };
}

interface SmartTaxOrganizerProcessingStatusProps {
  status: Exclude<ProcessingStatus, 'idle'>;
  errorMessage: string | null;
}

const SmartTaxOrganizerProcessingStatus: React.FC<
  SmartTaxOrganizerProcessingStatusProps
> = ({ status, errorMessage }) => {
  if (status === 'failed') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="warning" sx={{ fontSize: 20 }} />
            <Typography variant="body1" fontWeight={600}>
              Smart Tax Organizer submission failed
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {errorMessage ?? 'The uploaded organizer could not be processed.'}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (status === 'completed') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success" sx={{ alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon icon="check" sx={{ fontSize: 20 }} />
            <Typography variant="body1" fontWeight={600}>
              Organizer processed successfully!
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Opening the organizer request...
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="info" sx={{ alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body1" fontWeight={600}>
            {status === 'uploading'
              ? 'Uploading organizer...'
              : 'Processing your organizer...'}
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
};

interface SmartTaxOrganizerModalContentProps {
  open: boolean;
  onClose: () => void;
  onComplete: (result: SmartTaxOrganizerCompletionResult) => void;
}

const SmartTaxOrganizerModalContent: React.FC<
  SmartTaxOrganizerModalContentProps
> = ({ open, onClose, onComplete }) => {
  const [screen, setScreen] = useState<'education' | 'upload'>('education');
  const {
    templates,
    selectedTemplateUuid,
    handleTemplateChange,
    handleDrop,
    handleSampleSelect,
    uploadSelectedSample,
    isLoadingTemplates,
    isUploadInProgress,
    processingStatus,
    processingError,
    canStageFiles,
    canUpload,
    selectedSampleKey,
    isLoadingSample,
    isTaxReturnPilot,
  } = useSmartTaxOrganizerUpload(open, onComplete);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
        <IconButton size="small" onClick={handleClose} aria-label="Close">
          <Icon icon="close" fontSize="small" />
        </IconButton>
      </Box>

      {processingStatus === 'idle' && screen === 'education' ? (
        <Stack spacing={3} sx={{ p: 4 }}>
          <Box>
            <Typography variant="h3" fontWeight={700}>
              {isTaxReturnPilot
                ? 'Create From a Tax Return'
                : 'Meet the Smart Tax Organizer'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {isTaxReturnPilot
                ? 'Use a prior-year Form 1040 to start a personalized, client-friendly digital experience.'
                : 'Turn the organizer from your tax software into a personalized, client-friendly digital experience.'}
            </Typography>
          </Box>
          {(isTaxReturnPilot
            ? RETURN_ORGANIZER_BENEFITS
            : ORGANIZER_BENEFITS
          ).map(({ icon, title, description }) => (
            <Paper
              key={title}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}
            >
              <Icon icon={icon} />
              <Box>
                <Typography fontWeight={700}>{title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
              </Box>
            </Paper>
          ))}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              Takes less than a minute
            </Typography>
            <Button variant="contained" onClick={() => setScreen('upload')}>
              Next
            </Button>
          </Stack>
        </Stack>
      ) : processingStatus === 'idle' ? (
        <Stack spacing={3} sx={{ p: 3, bgcolor: 'background.default' }}>
          {!canStageFiles && (
            <Alert severity="warning">
              Smart Tax Organizer templates are required before creating an
              organizer.
            </Alert>
          )}
          {processingError && (
            <Alert severity="warning">{processingError}</Alert>
          )}
          <FormControl fullWidth size="small">
            <InputLabel id="smart-tax-organizer-template-label">
              Organizer Template
            </InputLabel>
            <Select
              labelId="smart-tax-organizer-template-label"
              label="Organizer Template"
              value={selectedTemplateUuid}
              onChange={handleTemplateChange}
              disabled={
                !canStageFiles ||
                isLoadingTemplates ||
                isUploadInProgress ||
                isLoadingSample
              }
              inputProps={{ 'aria-label': 'Organizer Template' }}
            >
              {templates.map((template) => (
                <MenuItem key={template.uuid} value={template.uuid}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
              {isTaxReturnPilot ? 'Try a return sample' : 'Try a sample'}
            </Typography>
            <Stack
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1,
              }}
            >
              {(isTaxReturnPilot
                ? [SYNTHETIC_RETURN_SAMPLE]
                : ORGANIZER_SAMPLES
              ).map((sample) => (
                <Button
                  key={sample.key}
                  variant={
                    selectedSampleKey === sample.key ? 'contained' : 'outlined'
                  }
                  onClick={() => void handleSampleSelect(sample)}
                  disabled={
                    !canStageFiles ||
                    isLoadingTemplates ||
                    isUploadInProgress ||
                    isLoadingSample
                  }
                >
                  {sample.label}
                </Button>
              ))}
            </Stack>
            {selectedSampleKey && (
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={uploadSelectedSample}
                disabled={!canUpload || isUploadInProgress || isLoadingSample}
              >
                Upload selected sample
              </Button>
            )}
          </Stack>
          {isTaxReturnPilot ? (
            <SmartTaxReturnUploadCard
              onDrop={handleDrop}
              disabled={!canUpload || isUploadInProgress || isLoadingSample}
              multiple={false}
              maxFiles={MAX_FILES}
              compact
            />
          ) : (
            <SmartTaxOrganizerUploadCard
              onDrop={handleDrop}
              disabled={!canUpload || isUploadInProgress || isLoadingSample}
              multiple={false}
              maxFiles={MAX_FILES}
            />
          )}
        </Stack>
      ) : (
        <SmartTaxOrganizerProcessingStatus
          status={processingStatus}
          errorMessage={processingError}
        />
      )}
    </Dialog>
  );
};

export const SmartTaxOrganizerModal: React.FC<SmartTaxOrganizerModalProps> = ({
  open,
  onClose,
  onComplete,
}) => {
  return (
    <SmartTaxOrganizerModalContent
      key={String(open)}
      open={open}
      onClose={onClose}
      onComplete={onComplete}
    />
  );
};
